import React from "react";
import {
  Pizza,
  Bike,
  Edit,
  Trash2,
  Clock,
  Calendar,
  Users,
  Store,
} from "lucide-react";
import { BrazilDate } from "../../../../utils/BrazilDate";

interface Usuario {
  id: number;
  nome: string;
  sobrenome?: string;
  tempoMedio?: string;
}

interface Loja {
  id: number;
  nome: string;
}

interface Meta {
  id: number;
  tipo: string;
  descricao: string;
  tempo: string;
  dataInicio: string;
  dataFim: string;
  ativo: boolean;
  loja?: Loja;
  usuarios?: Usuario[];
}

interface MetaCardProps {
  meta: Meta;
  onEdit: () => void;
  onDelete: () => void;
}

export const MetaCard: React.FC<MetaCardProps> = ({
  meta,
  onEdit,
  onDelete,
}) => {
  const getMetaTypeLabel = (tipo: string) => {
    return tipo === "producao" ? "Produção" : "Entrega";
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return BrazilDate.formatBR(date, false);
  };

  return (
    <div className="rounded-xl border border-neutral-100 bg-white p-6 shadow-lg transition-shadow duration-300 hover:shadow-xl">
      <div className="mb-4 flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div
            className={`rounded-lg p-2 ${meta.tipo === "producao" ? "bg-orange-100" : "bg-blue-100"}`}
          >
            {meta.tipo === "producao" ? (
              <Pizza className="text-orange-600" size={20} />
            ) : (
              <Bike className="text-blue-600" size={20} />
            )}
          </div>
          <div>
            <h3 className="text-lg font-bold text-neutral-900">
              Meta de {getMetaTypeLabel(meta.tipo)}
            </h3>
            <p className="text-sm text-neutral-500">{meta.descricao}</p>
          </div>
        </div>

        <div className="flex gap-1">
          <button
            onClick={onEdit}
            className="rounded-lg p-2 text-blue-500 transition-colors hover:bg-blue-50 hover:text-blue-700"
          >
            <Edit size={16} />
          </button>
          <button
            onClick={onDelete}
            className="rounded-lg p-2 text-red-500 transition-colors hover:bg-red-50 hover:text-red-700"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      <div className="mb-4 rounded-lg bg-gradient-to-r from-red-50 to-red-100 p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-3xl font-bold text-primary">{meta.tempo}</p>
            <p className="text-sm text-red-700">Tempo meta</p>
          </div>
          <Clock className="text-primary opacity-20" size={40} />
        </div>
      </div>

      <div className="mb-4">
        <div className="mb-2 flex items-center gap-2">
          <Calendar size={14} className="text-neutral-400" />
          <span className="text-xs font-medium text-neutral-500">
            {formatDate(meta.dataInicio)} - {formatDate(meta.dataFim)}
          </span>
        </div>

        {meta.loja && (
          <div className="mb-2 flex items-center gap-2">
            <Store size={14} className="text-neutral-400" />
            <span className="text-xs font-medium text-neutral-500">
              {meta.loja.nome}
            </span>
          </div>
        )}
      </div>

      <div className="border-t pt-4">
        <div className="mb-3 flex items-center gap-2">
          <Users size={16} className="text-neutral-500" />
          <span className="text-sm font-semibold text-neutral-700">
            {meta.usuarios?.length || 0} usuário
            {(meta.usuarios?.length || 0) !== 1 ? "s" : ""} vinculado
            {(meta.usuarios?.length || 0) !== 1 ? "s" : ""}
          </span>
        </div>

        {meta.usuarios && meta.usuarios.length > 0 && (
          <div className="grid max-h-32 grid-cols-1 gap-2 overflow-y-auto">
            {meta.usuarios.map((usuario) => (
              <div
                key={usuario.id}
                className="flex items-center justify-between rounded-lg bg-neutral-50 p-2"
              >
                <div className="flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary">
                    <span className="text-xs font-bold text-white">
                      {usuario.nome.charAt(0)}
                      {usuario.sobrenome?.charAt(0) || ""}
                    </span>
                  </div>
                  <span className="text-sm font-medium text-neutral-700">
                    {usuario.nome} {usuario.sobrenome || ""}
                  </span>
                </div>
                <span className="font-mono text-xs text-neutral-600">
                  {usuario.tempoMedio || "00:00:00"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4 border-t pt-3">
        <div
          className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
            meta.ativo
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          {meta.ativo ? "Ativo" : "Inativo"}
        </div>
      </div>
    </div>
  );
};
